import { parseWholeQty } from '../utils';

export const PAYMENT_TYPE_OPTIONS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CREDIT', label: 'Credit' },
];

function trimmedOrUndefined(value) {
  if (value === undefined || value === null) return undefined;
  const s = String(value).trim();
  return s === '' ? undefined : s;
}

function numberOrUndefined(value) {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Pre-submit validation — mirrors backend rules so the common failure modes never round-trip. */
export function validateReplacementGrnForm(form, { pendingQty } = {}) {
  const grnNumber = (form.grn_number || '').trim();
  if (!grnNumber) return { field: 'grn_number', message: 'Replacement GRN number is required' };
  if (grnNumber.length > 80) {
    return { field: 'grn_number', message: 'Replacement GRN number cannot exceed 80 characters' };
  }

  const qty = parseWholeQty(form.received_quantity);
  if (!qty) {
    return { field: 'received_quantity', message: 'Received quantity must be a whole number greater than 0' };
  }
  if (pendingQty != null && qty > pendingQty) {
    return { field: 'received_quantity', message: `Cannot exceed pending quantity (${pendingQty})` };
  }

  if (form.expiry_date) {
    const today = new Date().toISOString().slice(0, 10);
    if (form.expiry_date < today) {
      return { field: 'expiry_date', message: 'Expiry date cannot be in the past' };
    }
    if (form.manufacturing_date && form.expiry_date < form.manufacturing_date) {
      return { field: 'expiry_date', message: 'Expiry date cannot be before manufacturing date' };
    }
  }

  const mrp = numberOrUndefined(form.mrp);
  const saleRate = numberOrUndefined(form.sale_rate);
  if (mrp != null && saleRate != null && saleRate > mrp) {
    return { field: 'sale_rate', message: 'Sale rate cannot exceed MRP' };
  }

  return null;
}

/**
 * Build the POST body — only fields the user actually filled are included, so backend
 * defaults/auto-fill kick in cleanly for everything else.
 */
export function buildReplacementGrnBody(form) {
  const body = {
    grn_number: form.grn_number.trim(),
    received_quantity: parseWholeQty(form.received_quantity),
  };

  const stringFields = [
    'batch_number', 'manufacturer_batch_number', 'invoice_number',
    'delivery_challan_number', 'gate_entry_number', 'place_of_supply',
    'external_po_reference', 'remarks',
  ];
  stringFields.forEach((key) => {
    const v = trimmedOrUndefined(form[key]);
    if (v !== undefined) body[key] = v;
  });

  const dateFields = [
    'manufacturing_date', 'expiry_date', 'invoice_date',
    'delivery_date', 'grn_date', 'gate_entry_date',
  ];
  dateFields.forEach((key) => {
    if (form[key]) body[key] = form[key];
  });

  const numberFields = [
    'mrp', 'purchase_rate', 'sale_rate', 'tax_percent',
    'free_quantity', 'discount_percent', 'discount_amount',
  ];
  numberFields.forEach((key) => {
    const n = numberOrUndefined(form[key]);
    if (n !== undefined) body[key] = n;
  });

  if (form.payment_type) body.payment_type = form.payment_type;

  return body;
}

/**
 * Map a backend error to { field, message } so the form can show it inline where it
 * belongs (per the API spec's documented error messages) instead of only a banner.
 */
export function formatReplacementGrnError(err, fallback = 'Failed to create replacement GRN') {
  const message = err?.apiResponse?.message || err?.message || fallback;

  if (err?.status === 404) {
    return {
      field: null,
      message: 'GRN item not found. This row may be stale — refresh the dashboard and try again.',
      shouldCloseForm: true,
    };
  }
  if (err?.status === 422) {
    if (/already exists/i.test(message)) {
      return { field: 'grn_number', message: 'This GRN number already exists, please use a different one.' };
    }
    if (/exceeds pending_replacement_qty/i.test(message)) {
      return { field: 'received_quantity', message };
    }
    if (/no rejected quantity/i.test(message)) {
      return {
        field: null,
        message: `${message} — the dashboard data may be stale, try refreshing.`,
        shouldCloseForm: true,
      };
    }
    if (/purchase order.*is CLOSED/i.test(message) || /purchase order.*is CANCELLED/i.test(message)) {
      return { field: null, message, shouldCloseForm: true };
    }
    return { field: null, message };
  }
  return { field: null, message };
}
